import type {
  TransparencyProject,
  CreateTransparencyProjectDTO,
  UpdateTransparencyProjectDTO,
  TransparencyTimelineUpdate,
  CreateTimelineUpdateDTO,
  TransparencyComment,
  TransparencyCommentType,
  CreateTransparencyCommentDTO,
  TransparencyProjectImage,
  TransparencyProjectDocument,
  AddProjectImageDTO,
  AddProjectDocumentDTO,
  TransparencyDashboardStats,
} from '@yawi/types';
import { TransparencyProjectCategory, TransparencyProjectPriority, TransparencyProjectStatus } from '@yawi/types';
import type {
  ITransparencyProjectRepository,
  TransparencyProjectQueryParams,
} from '@yawi/domain';
import { paginateArray, DEFAULT_PAGE, DEFAULT_PAGE_SIZE } from '@yawi/domain';

const MOCK_TRANSPARENCY_PROJECTS: TransparencyProject[] = [
  {
    id: 'transp-1',
    title: 'Remodelación de alberca',
    shortDescription: 'Renovación completa del área de alberca y camastros',
    description:
      'Se realizará la renovación completa del área de alberca, incluyendo nuevo recubrimiento, sistema de filtrado y área de camastros.',
    category: TransparencyProjectCategory.AMENITIES,
    priority: TransparencyProjectPriority.HIGH,
    status: TransparencyProjectStatus.IN_PROGRESS,
    visibility: 'PUBLISHED',
    progress: 45,
    startDate: '2026-06-01',
    estimatedCompletionDate: '2026-09-01',
    actualCompletionDate: null,
    responsiblePerson: 'Ing. Carlos Ramírez',
    communityLocation: 'Área de alberca principal',
    budget: '450000.00',
    finalCost: null,
    coverImageFileId: null,
    coverImageUrl: null,
    commentsEnabled: true,
    questionsEnabled: true,
    reactionsEnabled: false,
    lastActivityAt: '2026-07-10T10:00:00.000Z',
    lastActivityTitle: 'Retiro del recubrimiento anterior completado',
    createdBy: 'admin-1',
    createdAt: '2026-05-20T10:00:00.000Z',
    updatedAt: '2026-07-15T10:00:00.000Z',
  },
  {
    id: 'transp-2',
    title: 'Instalación de cámaras de seguridad',
    shortDescription: 'Ampliación del circuito cerrado de vigilancia',
    description: 'Se instalarán 12 cámaras adicionales en puntos ciegos identificados por el comité de seguridad.',
    category: TransparencyProjectCategory.SECURITY,
    priority: TransparencyProjectPriority.URGENT,
    status: TransparencyProjectStatus.COMPLETED,
    visibility: 'PUBLISHED',
    progress: 100,
    startDate: '2026-03-01',
    estimatedCompletionDate: '2026-04-01',
    actualCompletionDate: '2026-03-28',
    responsiblePerson: 'Seguridad Hommi',
    communityLocation: 'Perímetro y accesos',
    budget: '180000.00',
    finalCost: '175500.00',
    coverImageFileId: null,
    coverImageUrl: null,
    commentsEnabled: true,
    questionsEnabled: false,
    reactionsEnabled: false,
    lastActivityAt: '2026-03-28T10:00:00.000Z',
    lastActivityTitle: 'Instalación finalizada y probada',
    createdBy: 'admin-1',
    createdAt: '2026-02-15T10:00:00.000Z',
    updatedAt: '2026-03-28T10:00:00.000Z',
  },
];

export class MockTransparencyProjectRepository implements ITransparencyProjectRepository {
  private projects: TransparencyProject[] = [...MOCK_TRANSPARENCY_PROJECTS];
  private timeline: TransparencyTimelineUpdate[] = [
    {
      id: 'update-1',
      projectId: 'transp-1',
      title: 'Retiro del recubrimiento anterior completado',
      description: 'Se terminó de retirar el recubrimiento antiguo de la alberca.',
      progressChange: 45,
      statusChange: null,
      authorId: 'admin-1',
      images: [],
      documents: [],
      createdAt: '2026-07-10T10:00:00.000Z',
    },
    {
      id: 'update-2',
      projectId: 'transp-1',
      title: 'Inicio de obra',
      description: null,
      progressChange: 10,
      statusChange: TransparencyProjectStatus.IN_PROGRESS,
      authorId: 'admin-1',
      images: [],
      documents: [],
      createdAt: '2026-06-01T10:00:00.000Z',
    },
  ];
  private comments: TransparencyComment[] = [];
  private links: { projectId: string; announcementId?: string; pollId?: string }[] = [];
  private images: TransparencyProjectImage[] = [];
  private documents: TransparencyProjectDocument[] = [];

  private delay(ms: number) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  private countsFor(projectId: string) {
    const projectComments = this.comments.filter((c) => c.projectId === projectId);
    return {
      comments: projectComments.filter((c) => c.type === 'COMMENT').length,
      questions: projectComments.filter((c) => c.type === 'QUESTION').length,
      images: this.images.filter((i) => i.projectId === projectId).length,
      documents: this.documents.filter((d) => d.projectId === projectId).length,
    };
  }

  private applyFilters(list: TransparencyProject[], params?: TransparencyProjectQueryParams) {
    let filtered = [...list];
    if (params?.search) {
      const search = params.search.toLowerCase();
      filtered = filtered.filter(
        (p) =>
          p.title.toLowerCase().includes(search) ||
          (p.shortDescription ?? '').toLowerCase().includes(search)
      );
    }
    if (params?.category) filtered = filtered.filter((p) => p.category === params.category);
    if (params?.status) filtered = filtered.filter((p) => p.status === params.status);
    if (params?.priority) filtered = filtered.filter((p) => p.priority === params.priority);

    if (params?.view === 'active') {
      filtered = filtered.filter(
        (p) =>
          p.visibility !== 'ARCHIVED' &&
          p.status !== TransparencyProjectStatus.COMPLETED &&
          p.status !== TransparencyProjectStatus.CANCELLED
      );
    } else if (params?.view === 'completed') {
      filtered = filtered.filter((p) => p.status === TransparencyProjectStatus.COMPLETED);
    } else if (params?.view === 'archived') {
      filtered = filtered.filter((p) => p.visibility === 'ARCHIVED');
    }

    return filtered
      .map((p) => ({ ...p, counts: this.countsFor(p.id) }))
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  }

  async getProjects(params?: TransparencyProjectQueryParams) {
    await this.delay(300);
    const filtered = this.applyFilters(this.projects, params);
    return paginateArray(filtered, params?.page ?? DEFAULT_PAGE, params?.limit ?? DEFAULT_PAGE_SIZE);
  }

  async getPublishedProjects(params?: TransparencyProjectQueryParams) {
    await this.delay(300);
    const published = this.projects.filter((p) => p.visibility === 'PUBLISHED');
    const filtered = this.applyFilters(published, params);
    return paginateArray(filtered, params?.page ?? DEFAULT_PAGE, params?.limit ?? DEFAULT_PAGE_SIZE);
  }

  async getHistoryProjects(params?: TransparencyProjectQueryParams) {
    await this.delay(300);
    const completed = this.projects.filter((p) => p.status === TransparencyProjectStatus.COMPLETED);
    const filtered = this.applyFilters(completed, params);
    return paginateArray(filtered, params?.page ?? DEFAULT_PAGE, params?.limit ?? DEFAULT_PAGE_SIZE);
  }

  async getProjectById(id: string): Promise<TransparencyProject | null> {
    await this.delay(200);
    return this.projects.find((p) => p.id === id) || null;
  }

  async createProject(data: CreateTransparencyProjectDTO): Promise<TransparencyProject> {
    await this.delay(500);
    const now = new Date().toISOString();
    const newProject: TransparencyProject = {
      id: `transp-${Date.now()}`,
      title: data.title,
      shortDescription: data.shortDescription ?? null,
      description: data.description,
      category: data.category ?? TransparencyProjectCategory.OTHER,
      priority: data.priority ?? TransparencyProjectPriority.MEDIUM,
      status: data.status ?? TransparencyProjectStatus.PLANNING,
      visibility: 'DRAFT',
      progress: 0,
      startDate: data.startDate ?? null,
      estimatedCompletionDate: data.estimatedCompletionDate ?? null,
      actualCompletionDate: data.actualCompletionDate ?? null,
      responsiblePerson: data.responsiblePerson ?? null,
      communityLocation: data.communityLocation ?? null,
      budget: data.budget ?? null,
      finalCost: null,
      coverImageFileId: data.coverImageFileId ?? null,
      coverImageUrl: null,
      commentsEnabled: data.commentsEnabled ?? true,
      questionsEnabled: data.questionsEnabled ?? true,
      reactionsEnabled: false,
      lastActivityAt: null,
      lastActivityTitle: null,
      createdBy: 'mock-user',
      createdAt: now,
      updatedAt: now,
    };
    this.projects.unshift(newProject);
    return newProject;
  }

  async updateProject(id: string, data: UpdateTransparencyProjectDTO): Promise<TransparencyProject> {
    await this.delay(500);
    const index = this.projects.findIndex((p) => p.id === id);
    if (index === -1) throw new Error('Project not found');
    const updated = { ...this.projects[index], ...data, updatedAt: new Date().toISOString() };
    this.projects[index] = updated;
    return updated;
  }

  async publishProject(id: string): Promise<TransparencyProject> {
    await this.delay(300);
    const index = this.projects.findIndex((p) => p.id === id);
    if (index === -1) throw new Error('Project not found');
    this.projects[index] = { ...this.projects[index], visibility: 'PUBLISHED', updatedAt: new Date().toISOString() };
    return this.projects[index];
  }

  async notifyResidents(id: string): Promise<void> {
    await this.delay(300);
    const project = this.projects.find((p) => p.id === id);
    if (!project) throw new Error('Project not found');
    if (project.visibility !== 'PUBLISHED') {
      throw new Error('Solo se pueden notificar proyectos publicados');
    }
  }

  async archiveProject(id: string): Promise<TransparencyProject> {
    await this.delay(300);
    const index = this.projects.findIndex((p) => p.id === id);
    if (index === -1) throw new Error('Project not found');
    this.projects[index] = { ...this.projects[index], visibility: 'ARCHIVED', updatedAt: new Date().toISOString() };
    return this.projects[index];
  }

  async deleteProject(id: string): Promise<void> {
    await this.delay(400);
    await this.archiveProject(id);
  }

  async getTimeline(projectId: string): Promise<TransparencyTimelineUpdate[]> {
    await this.delay(200);
    return this.timeline
      .filter((u) => u.projectId === projectId)
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  }

  async addTimelineUpdate(
    projectId: string,
    data: CreateTimelineUpdateDTO
  ): Promise<TransparencyTimelineUpdate> {
    await this.delay(400);
    const now = new Date().toISOString();
    const updateId = `update-${Date.now()}`;

    const images: TransparencyProjectImage[] = (data.imageFileIds ?? []).map((fileId, i) => ({
      id: `img-${Date.now()}-${i}`,
      projectId,
      fileId,
      url: null,
      isBeforeAfter: 'NONE',
      displayOrder: i,
      updateId,
      createdAt: now,
    }));
    const documents: TransparencyProjectDocument[] = (data.documentFileIds ?? []).map(
      (fileId, i) => ({
        id: `doc-${Date.now()}-${i}`,
        projectId,
        fileId,
        url: null,
        label: null,
        updateId,
        createdAt: now,
      })
    );
    this.images.push(...images);
    this.documents.push(...documents);

    const update: TransparencyTimelineUpdate = {
      id: updateId,
      projectId,
      title: data.title,
      description: data.description ?? null,
      progressChange: data.progressChange ?? null,
      statusChange: data.statusChange ?? null,
      authorId: 'mock-user',
      images,
      documents,
      createdAt: now,
    };
    this.timeline.unshift(update);

    const index = this.projects.findIndex((p) => p.id === projectId);
    if (index !== -1) {
      this.projects[index] = {
        ...this.projects[index],
        ...(data.progressChange !== undefined && { progress: data.progressChange }),
        ...(data.statusChange !== undefined && { status: data.statusChange }),
        lastActivityAt: now,
        lastActivityTitle: data.title,
        updatedAt: now,
      };
    }

    return update;
  }

  async getProjectImages(projectId: string): Promise<TransparencyProjectImage[]> {
    await this.delay(150);
    return this.images
      .filter((i) => i.projectId === projectId)
      .sort((a, b) => a.displayOrder - b.displayOrder);
  }

  async addProjectImage(
    projectId: string,
    data: AddProjectImageDTO
  ): Promise<TransparencyProjectImage> {
    await this.delay(250);
    const image: TransparencyProjectImage = {
      id: `img-${Date.now()}`,
      projectId,
      fileId: data.fileId,
      url: null,
      isBeforeAfter: data.isBeforeAfter ?? 'NONE',
      displayOrder: data.displayOrder ?? 0,
      updateId: null,
      createdAt: new Date().toISOString(),
    };
    this.images.push(image);
    return image;
  }

  async deleteProjectImage(projectId: string, imageId: string): Promise<void> {
    await this.delay(200);
    this.images = this.images.filter((i) => !(i.projectId === projectId && i.id === imageId));
  }

  async getProjectDocuments(projectId: string): Promise<TransparencyProjectDocument[]> {
    await this.delay(150);
    return this.documents.filter((d) => d.projectId === projectId);
  }

  async addProjectDocument(
    projectId: string,
    data: AddProjectDocumentDTO
  ): Promise<TransparencyProjectDocument> {
    await this.delay(250);
    const document: TransparencyProjectDocument = {
      id: `doc-${Date.now()}`,
      projectId,
      fileId: data.fileId,
      url: null,
      label: data.label ?? null,
      updateId: null,
      createdAt: new Date().toISOString(),
    };
    this.documents.push(document);
    return document;
  }

  async deleteProjectDocument(projectId: string, documentId: string): Promise<void> {
    await this.delay(200);
    this.documents = this.documents.filter(
      (d) => !(d.projectId === projectId && d.id === documentId)
    );
  }

  async getComments(projectId: string, type?: TransparencyCommentType): Promise<TransparencyComment[]> {
    await this.delay(200);
    return this.comments.filter((c) => c.projectId === projectId && (!type || c.type === type));
  }

  async addComment(
    projectId: string,
    data: CreateTransparencyCommentDTO
  ): Promise<TransparencyComment> {
    await this.delay(300);
    const comment: TransparencyComment = {
      id: `comment-${Date.now()}`,
      projectId,
      type: data.type,
      userId: 'mock-user',
      userName: 'Usuario Demo',
      text: data.text,
      isAdmin: false,
      createdAt: new Date().toISOString(),
    };
    this.comments.push(comment);
    return comment;
  }

  async deleteComment(projectId: string, commentId: string): Promise<void> {
    await this.delay(200);
    this.comments = this.comments.filter((c) => !(c.projectId === projectId && c.id === commentId));
  }

  async getLinks(projectId: string): Promise<{ announcementIds: string[]; pollIds: string[] }> {
    await this.delay(150);
    const projectLinks = this.links.filter((l) => l.projectId === projectId);
    return {
      announcementIds: projectLinks.filter((l) => l.announcementId).map((l) => l.announcementId!),
      pollIds: projectLinks.filter((l) => l.pollId).map((l) => l.pollId!),
    };
  }

  async linkAnnouncement(projectId: string, announcementId: string): Promise<void> {
    await this.delay(200);
    if (!this.links.some((l) => l.projectId === projectId && l.announcementId === announcementId)) {
      this.links.push({ projectId, announcementId });
    }
  }

  async unlinkAnnouncement(projectId: string, announcementId: string): Promise<void> {
    await this.delay(200);
    this.links = this.links.filter((l) => !(l.projectId === projectId && l.announcementId === announcementId));
  }

  async linkSurvey(projectId: string, pollId: string): Promise<void> {
    await this.delay(200);
    if (!this.links.some((l) => l.projectId === projectId && l.pollId === pollId)) {
      this.links.push({ projectId, pollId });
    }
  }

  async unlinkSurvey(projectId: string, pollId: string): Promise<void> {
    await this.delay(200);
    this.links = this.links.filter((l) => !(l.projectId === projectId && l.pollId === pollId));
  }

  async getDashboardStats(): Promise<TransparencyDashboardStats> {
    await this.delay(150);
    const total = this.projects.length;
    const byStatus = Object.values(TransparencyProjectStatus).reduce(
      (acc, status) => {
        acc[status] = this.projects.filter((p) => p.status === status).length;
        return acc;
      },
      {} as Record<string, number>
    );
    const completionRate =
      total > 0 ? Math.round((byStatus[TransparencyProjectStatus.COMPLETED] / total) * 100) : 0;
    const live = this.projects.filter((p) => p.status !== TransparencyProjectStatus.CANCELLED);
    const averageProgress = live.length
      ? Math.round(live.reduce((sum, p) => sum + p.progress, 0) / live.length)
      : 0;
    return { total, byStatus, completionRate, averageProgress };
  }
}
